import { ChangeDetectionStrategy, Component, computed, input, model, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-zoom-control',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslateModule],
  template: `
    <button mat-icon-button (click)="zoomOut()" [matTooltip]="'Global.ZoomOut' | translate" [disabled]="zoomScale() <= minScale()">
      <mat-icon>zoom_out</mat-icon>
    </button>
    <input
      type="text"
      class="zoom-input"
      [value]="zoomPercentText()"
      (focus)="onZoomFocus($event)"
      (blur)="onZoomBlur($event)"
      (keydown)="onZoomKeyDown($event)"
    />
    <button mat-icon-button (click)="zoomIn()" [matTooltip]="'Global.ZoomIn' | translate" [disabled]="zoomScale() >= maxScale()">
      <mat-icon>zoom_in</mat-icon>
    </button>
    <button mat-icon-button (click)="resetZoom()" [matTooltip]="'Global.ResetZoom' | translate">
      <mat-icon>restart_alt</mat-icon>
    </button>

    @if (showFullScreen()) {
      <div class="toolbar-divider"></div>
      <button mat-icon-button (click)="fullscreen.emit()" [matTooltip]="'Global.FullScreen' | translate">
        <mat-icon>fullscreen</mat-icon>
      </button>
    }

    @if (showClose()) {
      <div class="toolbar-divider"></div>
      <button mat-icon-button (click)="close.emit()" [matTooltip]="'Global.Cancel' | translate">
        <mat-icon>close</mat-icon>
      </button>
    }
  `,
  styles: [
    `
      :host {
        display: flex;
        align-items: center;
        gap: 6px;
        background: rgba(255, 255, 255, 0.9);
        backdrop-filter: blur(8px);
        padding: 4px 8px;
        border-radius: 12px;
        border: 1px solid rgba(0, 0, 0, 0.08);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);

        .toolbar-divider {
          width: 1px;
          height: 16px;
          background-color: rgba(0, 0, 0, 0.12);
          margin: 0 4px;
        }

        .zoom-input {
          font-size: 12px;
          font-weight: 700;
          color: var(--skz-text-muted);
          width: 44px;
          text-align: center;
          border: none;
          background: transparent;
          outline: none;
          padding: 2px 4px;
          border-radius: 6px;
          transition:
            background-color 0.2s,
            color 0.2s;

          &:hover,
          &:focus {
            background: rgba(0, 0, 0, 0.05);
            color: var(--skz-icon-color-blue);
          }
        }

        button {
          width: 28px !important;
          height: 28px !important;
          line-height: 28px !important;
          color: var(--skz-text-muted);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 !important;

          &:hover {
            color: var(--skz-icon-color-blue);
            background: var(--skz-highlighted-item-color-lighter);
          }

          &[disabled] {
            color: #c0c0c0;
            background: transparent;
          }

          mat-icon {
            font-size: 18px !important;
            width: 18px !important;
            height: 18px !important;
          }
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZoomControlComponent {
  zoomScale = model<number>(1.0);
  minScale = input<number>(0.3);
  maxScale = input<number>(5.0);
  showFullScreen = input<boolean>(false);
  showClose = input<boolean>(false);

  reset = output<void>();
  fullscreen = output<void>();
  close = output<void>();

  zoomPercentText = computed(() => Math.round(this.zoomScale() * 100) + '%');

  zoomIn() {
    const step = this.maxScale() > 3.5 ? 0.2 : 0.1;
    this.zoomScale.update((s) => Math.min(s + step, this.maxScale()));
  }

  zoomOut() {
    const step = this.maxScale() > 3.5 ? 0.2 : 0.1;
    this.zoomScale.update((s) => Math.max(s - step, this.minScale()));
  }

  resetZoom() {
    this.zoomScale.set(1.0);
    this.reset.emit();
  }

  onZoomFocus(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    input.value = Math.round(this.zoomScale() * 100).toString();
    input.select();
  }

  onZoomBlur(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    this.applyZoomText(input.value);
    input.value = this.zoomPercentText();
  }

  onZoomKeyDown(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Enter') {
      input.blur();
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      const cleanText = input.value.trim().replace('%', '');
      let val = parseInt(cleanText, 10);
      if (isNaN(val)) {
        val = Math.round(this.zoomScale() * 100);
      }
      const step = event.shiftKey ? 50 : 10;
      const minPercent = Math.round(this.minScale() * 100);
      const maxPercent = Math.round(this.maxScale() * 100);
      if (event.key === 'ArrowUp') {
        val = Math.min(val + step, maxPercent);
      } else {
        val = Math.max(val - step, minPercent);
      }
      input.value = val.toString();
      input.select();
      this.zoomScale.set(val / 100);
    }
  }

  private applyZoomText(text: string) {
    const cleanText = text.trim().replace('%', '');
    const val = parseFloat(cleanText);
    const minPercent = Math.round(this.minScale() * 100);
    const maxPercent = Math.round(this.maxScale() * 100);

    if (!isNaN(val)) {
      let scale = 1.0;
      if (val >= minPercent && val <= maxPercent) {
        scale = val / 100;
      } else if (val >= this.minScale() && val <= this.maxScale()) {
        scale = val;
      } else if (val < this.minScale() || val < minPercent) {
        scale = this.minScale();
      } else {
        scale = this.maxScale();
      }
      this.zoomScale.set(scale);
    }
  }
}
